'use client'

/**
 * MobileAssistantChat
 *
 * Phone-width replacement for AssistantWidget (which stays desktop/tablet
 * only): a full-width strip pinned to the bottom of the mobile shell that
 * expands the assistant chat to cover the whole screen when tapped.
 *
 * Conversation id + unread-dot state live in useAssistantWidgetState
 * (shared with AssistantWidget), so the user resumes the same conversation
 * across devices/layouts. The open state is intentionally NOT persisted —
 * on a phone the chat should never swallow the dashboard on load.
 */

import { useState, useEffect, useRef } from 'react'
import { ChevronUpIcon } from '@heroicons/react/24/outline'
import Logo from '@/components/ui/Logo'
import { AssistantPanel } from './AssistantPanel'
import { useAssistantWidgetState } from './useAssistantWidgetState'
import { useTranslations } from 'next-intl'

const STRIP_HEIGHT_PX = 56

/**
 * Bottom clearance the mobile shell must reserve so the strip never covers
 * the last card: the strip's content height plus the device safe-area inset
 * (home indicator), which the strip itself also grows by.
 */
export const MOBILE_CHAT_STRIP_CLEARANCE = `calc(${STRIP_HEIGHT_PX}px + env(safe-area-inset-bottom))`

export function MobileAssistantChat() {
  const t = useTranslations('assistant')
  const [isOpen, setIsOpen] = useState(false)

  const {
    mounted,
    activeConversationId,
    onConversationChange,
    isStreaming,
    showUnreadDot,
  } = useAssistantWidgetState({ isOpen })

  // Lock body scroll while the full-screen chat is open so the dashboard
  // underneath doesn't scroll along with the message list.
  useEffect(() => {
    if (!isOpen) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  // Escape closes the full-screen chat (hardware keyboards / accessibility).
  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen])

  // Move focus into the dialog when it opens, and restore it to the strip
  // when it closes, so keyboard/screen-reader users don't lose their place.
  const dialogRef = useRef<HTMLDivElement>(null)
  const stripRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.focus()
    } else {
      stripRef.current?.focus({ preventScroll: true })
    }
  }, [isOpen])

  if (!mounted) return null

  if (isOpen) {
    // Full-screen chat covering everything (including the top nav).
    return (
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('panel.title')}
        tabIndex={-1}
        className="fixed inset-0 z-[70] flex flex-col outline-none"
        style={{ background: 'var(--color-surface)' }}
      >
        <AssistantPanel
          activeConversationId={activeConversationId}
          isExpanded
          onToggleExpand={() => {}}
          hideExpandToggle
          onConversationChange={onConversationChange}
          onClose={() => setIsOpen(false)}
        />
      </div>
    )
  }

  // Bottom strip launcher. minHeight (not height) so the safe-area padding
  // grows the strip on notched devices instead of eating into its content.
  // z-40 keeps it above page content but below drawers/modals (z-50) so it
  // never covers a drawer's Save button; the expanded chat above is its own
  // full-screen dialog at z-[70].
  return (
    <button
      ref={stripRef}
      type="button"
      onClick={() => setIsOpen(true)}
      title={t('widget.openAssistant')}
      aria-label={t('widget.openAssistant')}
      className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-center gap-2 border-t"
      style={{
        minHeight: `${STRIP_HEIGHT_PX}px`,
        background: 'var(--color-surface)',
        borderColor: 'var(--color-border)',
        boxShadow: '0 -8px 24px rgba(0,0,0,0.15)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Gradient hairline pinned to the top of the strip — mirrors the
          assistant panel's lime→cyan bar so the strip reads as "the chat". */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0"
        style={{ height: '2px', background: 'var(--gradient-lime-cyan)' }}
      />

      {isStreaming && (
        <span
          aria-hidden="true"
          className="w-2 h-2 rounded-full animate-pulse"
          style={{ background: 'var(--color-lime)' }}
        />
      )}

      <Logo width={54} height={20} />

      <span
        className="text-sm font-semibold"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {t('widget.ask')}
      </span>

      {showUnreadDot && (
        <span
          aria-label={t('widget.unreadMessage')}
          className="w-2.5 h-2.5 rounded-full"
          style={{
            background: 'var(--color-pink)',
            border: '2px solid var(--color-surface)',
          }}
        />
      )}

      <ChevronUpIcon
        className="w-4 h-4"
        style={{ color: 'var(--color-text-muted)' }}
      />
    </button>
  )
}
